// src/judge/RotationOverview.tsx
//
// Přehled „Kde jsou rundy“ automatické rotace (krok 4, R4 – rozbor §5.5) pro
// hlavního rozhodčího a správce na obrazovce rozhodčího: na výběru
// stanoviště, čekací obrazovce a ve výběru střelce – nikdy u gongů.
// Runda → kde je (a/b hotovo), čeká-li na uvolnění stanoviště, kde začala
// (Počáteční rozložení, otázka 11), uvolněná stanoviště s „Vrátit“, chybějící
// výsledky prošlých stanovišť; rundy a stanoviště mimo rotaci.
// Sbalený (jedno ťuknutí), mezery okraji – ne flex gap (Chrome 80).

import React, { useState } from "react";
import { skippedResults, stationQueue, type RotationState } from "../utils/liveRotation";

type ResultsLike = Parameters<typeof skippedResults>[1];

interface Props {
  state: RotationState;
  stations: ReadonlyArray<{ id: string; number: number; name?: string }>;
  results: ResultsLike;
  /** Hlavní rozhodčí a správce: „Vrátit“ uvolnění. */
  canUndo: boolean;
  onUndo: (stationId: string, roundNumber: number) => void;
}

export const RotationOverview: React.FC<Props> = ({ state, stations, results, canUndo, onUndo }) => {
  const [open, setOpen] = useState(false);
  const label = (id: string) => {
    const st = stations.find((s) => s.id === id);
    return st ? `St. ${st.number}` : "smazané st.";
  };
  const outside = state.stationsOutside.map(label);
  return (
    <div className="card mt-3 mx-auto text-start" style={{ maxWidth: 480 }}>
      <button
        type="button"
        className="btn btn-link text-start text-decoration-none d-flex align-items-center px-3 py-2"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <i className="bi bi-diagram-3 me-2" aria-hidden="true" />
        <span className="flex-grow-1 fw-semibold">Kde jsou rundy</span>
        <i className={`bi bi-chevron-${open ? "up" : "down"}`} aria-hidden="true" />
      </button>
      {open && (
        <div className="card-body pt-0 small">
          <ul className="list-unstyled mb-0">
            {state.rounds.map((r) => {
              const stop = r.pos !== null ? r.stops[r.pos] : null;
              const waiting = stop !== null && stationQueue(state, stop.stationId)[0]?.round !== r.round;
              const where = r.empty
                ? "bez aktivních střelců"
                : r.start === null
                ? "mimo rotaci – rundu vybírají rozhodčí ručně"
                : r.finished
                ? "hotovo na všech stanovištích"
                : stop
                ? `${label(stop.stationId)} (${stop.doneCount}/${stop.expected.length})${waiting ? " · čeká na uvolnění" : ""}`
                : "";
              const released = r.stops.filter((s) => s.released);
              const missing = r.empty ? [] : skippedResults(r, results);
              return (
                <li key={r.round} className="py-2 border-top" data-round={r.round}>
                  <div className="d-flex align-items-center">
                    <span className="badge bg-success me-2">R{r.round}</span>
                    <span className="fw-semibold">{where}</span>
                  </div>
                  {r.start !== null && r.route.length > 0 && <div className="text-muted">Start: {label(r.route[0])}</div>}
                  {released.map((s) => (
                    <div key={s.stationId} className="d-flex align-items-center mt-1">
                      <span className="flex-grow-1">
                        Uvolněno: {label(s.stationId)}
                        {s.releasedMissing.length > 0 ? ` – chybí ${s.releasedMissing.map((n) => `#${n}`).join(", ")}` : " – odstřílela"}
                      </span>
                      {canUndo && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary py-0 ms-2"
                          onClick={() => onUndo(s.stationId, r.round)}
                          aria-label={`Vrátit uvolnění rundy ${r.round} na ${label(s.stationId)}`}
                        >
                          Vrátit
                        </button>
                      )}
                    </div>
                  ))}
                  {missing.map((m) => (
                    <div key={m.stationId} className="text-danger mt-1">
                      Chybí výsledky: {label(m.stationId)} {m.startNumbers.map((n) => `#${n}`).join(", ")}
                    </div>
                  ))}
                </li>
              );
            })}
          </ul>
          {outside.length > 0 && (
            <div className="text-muted border-top pt-2">Mimo rotaci (přidané za běhu): {outside.join(", ")} – rundu tam rozhodčí vybírá ručně.</div>
          )}
        </div>
      )}
    </div>
  );
};
